import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, UrlTree } from '@angular/router';
import { AuthService, SwitchAccountsResponse } from '../auth/auth.service';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Routes } from '../../config/routes';
import environment from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { SimpleOrganization } from './organization.service';

@Injectable({
    providedIn: 'root',
})
export class OrganizationDataService
{
    private readonly orgLoadingSubject$ = new BehaviorSubject<boolean>(false);
    private readonly orgsListSubject$ = new BehaviorSubject<SimpleOrganization[]>([]);
    private readonly demoModeSubject$ = new BehaviorSubject<boolean>(false);

    constructor(
            private _router: Router,
            private _authService: AuthService,
    ) {
    }

    get orgList$(): Observable<SimpleOrganization[]> {
        return this.orgsListSubject$.asObservable();
    }

    get demoOrg$(): Observable<boolean> {
        return this.demoModeSubject$.asObservable();
    }

    get orgLoading$(): Observable<boolean> {
        return this.orgLoadingSubject$.asObservable();
    }

    /**
     * The org the user is currently viewing. Reads jwt.orgId, which store()
     * keeps in sync with the viewed org on every switch (SD-3086). The former
     * `orgSubject$` "displayed-org-first" layer (SD-7027) was never populated —
     * this getter always fell through to the JWT — so reading it directly is
     * behavior-identical. For a reactive view of the current org, inject
     * `OrgContextService` (the canonical org-context signal).
     */
    get orgId(): number {
        return LegFiJwtService.read()?.orgId;
    }

    switchOrganization(id: number, idParams: { [key: string]: number } = {}, redirectTarget?: any) {
        // this.isShowingLoadingModal = true;
        this.orgLoadingSubject$.next(true);

        if (redirectTarget && Array.isArray(redirectTarget) && redirectTarget.length === 2) {
            const tree: UrlTree = this._router.createUrlTree(redirectTarget);
            redirectTarget = tree.toString();
        }

        const params = {
            ...idParams,
            ...{siteId: environment.SiteId},
        };

        return this._authService.switchAccounts(id, params).pipe(
            map((data) => {
                const jwt = LegFiJwtService.read();

                // The cross-tab sync trigger used to be written here (SD-8428). It now fires from
                // LegFiJwtService.store() instead, which is where the org actually changes and is
                // therefore the only place that catches every path, including login (SD-10155).

                // get data from response
                let siteId = null;
                if (data.hasOwnProperty('siteId')) {
                    siteId = data.siteId;
                }
                let isExternalRedirect = false;
                let nonce: string = '';
                if (data.hasOwnProperty('external') && data.hasOwnProperty('nonce')) {
                    isExternalRedirect = true;
                    nonce = data.nonce;
                }

                // handle redirect target
                if (!redirectTarget) {
                    let tree: UrlTree;
                    if (!jwt.superUser || jwt.impersonatedBy) {
                        // admins and homeowners
                        if (jwt.admin && !params.hasOwnProperty('unitId')) {
                            tree = this._router.createUrlTree(['/app/organization/dashboard']);
                        } else if (jwt.unitId) {
                            tree = this._router.createUrlTree([`/app/unit/detail/${jwt.unitId}`]);
                        } else {
                            tree = this._router.createUrlTree(['/app/owner/dashboard']);
                        }
                    } else {
                        // supers
                        tree = this._router.createUrlTree(['/app/organization/dashboard']);
                    }
                    redirectTarget = tree.toString();
                }

                // return url/redirection based on data
                if (isExternalRedirect) {
                    return [
                        `${Routes.GetUiDomain(siteId)}/auth/login;orgId=${jwt.orgId};nonce=${nonce};uri=${encodeURIComponent(redirectTarget)}`,
                        true,
                    ];
                } else {
                    return [
                        `/redirect;uri=${encodeURIComponent(redirectTarget)}`,
                        false,
                    ];
                }
            }));
    }

    refreshOrgList(organizations: SimpleOrganization[]) {
        this.orgsListSubject$.next(organizations);
        this.toggleDemoMode(organizations.length === 1 && organizations[0].demo);
    }

    removeFromOrgList(organization: SimpleOrganization) {
        const orgs = this.orgsListSubject$.getValue().filter(org => org.id !== organization.id);
        this.orgsListSubject$.next([...orgs]);
    }

    toggleDemoMode(isActive = false) {
        this.demoModeSubject$.next(isActive);
    }
}
